import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../assets/styling/cookie-consent.css";
import {
  clearTrackerCookies,
  getEffectiveConsent,
  getStoredConsent,
  gpcDeniedCategories,
  hasDecided,
  hasGPC,
  setStoredConsent,
} from "../utils/consent";
import { applyConsent, revokeLoadedTrackers } from "../utils/trackerGating";

/**
 * Consent banner and preference centre.
 *
 * Contract this component upholds:
 * - Nothing nonessential loads until a category is granted here (see trackerGating).
 * - Reject is as reachable as Accept, and dismissing without choosing is not consent —
 *   there is no "X" that silently grants, and the banner persists until a real choice.
 * - The decision is revisitable at any time via the footer's "Your Privacy Choices".
 * - A GPC browser signal locks the affected categories off and says so.
 *
 * Open the preference centre from anywhere with:
 *   window.dispatchEvent(new CustomEvent("tbb:open-privacy-choices"))
 */

export const OPEN_PRIVACY_CHOICES_EVENT = "tbb:open-privacy-choices";

const CATEGORY_COPY = [
  {
    key: "necessary",
    title: "Strictly necessary",
    always: true,
    body: "Required for the site to work: security, spam prevention on our email form, remembering your quiz progress, and storing this privacy choice. Never used for advertising.",
  },
  {
    key: "analytics",
    title: "Analytics",
    body: "Google Analytics, so we can see which pages people find useful. Off until you turn it on.",
  },
  {
    key: "advertising",
    title: "Advertising",
    body: "The Meta (Facebook) pixel and the matching server-side events, used to measure our ads. Off until you turn it on.",
  },
];

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [gpc] = useState(() => hasGPC());
  const [draft, setDraft] = useState(() => {
    const stored = getStoredConsent();
    return {
      analytics: stored?.analytics === true,
      advertising: stored?.advertising === true,
    };
  });

  // On mount: apply an existing decision, or show the banner if there isn't one.
  useEffect(() => {
    if (hasDecided()) {
      applyConsent(getEffectiveConsent());
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  // Let the footer link reopen the preference centre after a decision was made.
  useEffect(() => {
    const open = () => {
      const stored = getStoredConsent();
      setDraft({
        analytics: stored?.analytics === true,
        advertising: stored?.advertising === true,
      });
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_PRIVACY_CHOICES_EVENT, open);
    return () => window.removeEventListener(OPEN_PRIVACY_CHOICES_EVENT, open);
  }, []);

  const commit = useCallback((choice) => {
    const previous = getEffectiveConsent();
    const effective = setStoredConsent(choice);

    // Downgrading a category that is already live: revoke, drop the identifiers,
    // then reload so the vendor script is genuinely gone from the page. A script
    // cannot be unloaded in place, and we would rather reload than claim a
    // tracker is off while it is still resident.
    const downgraded =
      (previous.analytics && !effective.analytics) ||
      (previous.advertising && !effective.advertising);

    if (downgraded) {
      revokeLoadedTrackers();
      clearTrackerCookies();
      window.location.reload();
      return;
    }

    applyConsent(effective);
    setShowPreferences(false);
    setVisible(false);
  }, []);

  const acceptAll = () =>
    commit({ analytics: true, advertising: true });
  const rejectAll = () =>
    commit({ analytics: false, advertising: false });
  const savePreferences = () => commit(draft);

  if (!visible) return null;

  const isLocked = (key) => gpc && gpcDeniedCategories.includes(key);

  return (
    <div
      className="tbb-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="tbb-consent-title"
    >
      <div className="tbb-consent__panel">
        <h2 id="tbb-consent-title" className="tbb-consent__title">
          {showPreferences ? "Your privacy choices" : "Cookies and tracking"}
        </h2>

        {gpc && (
          <p className="tbb-consent__gpc">
            Your browser is sending a Global Privacy Control signal. We are
            honouring it: analytics and advertising stay off, and you do not need
            to do anything else.
          </p>
        )}

        {!showPreferences && (
          <p className="tbb-consent__body">
            We use cookies that are necessary for this site to work. We would
            also like to use analytics and advertising cookies, but only if you
            agree. Nothing optional runs until you choose. Read our{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        )}

        {showPreferences && (
          <div className="tbb-consent__categories">
            {CATEGORY_COPY.map((category) => {
              const locked = isLocked(category.key);
              const checked = category.always
                ? true
                : locked
                  ? false
                  : draft[category.key];

              return (
                <div className="tbb-consent__category" key={category.key}>
                  <label className="tbb-consent__category-head">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={category.always || locked}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [category.key]: e.target.checked,
                        }))
                      }
                    />
                    <span className="tbb-consent__category-title">
                      {category.title}
                    </span>
                    {category.always && (
                      <span className="tbb-consent__tag">Always on</span>
                    )}
                    {locked && (
                      <span className="tbb-consent__tag">
                        Off — browser signal
                      </span>
                    )}
                  </label>
                  <p className="tbb-consent__category-body">{category.body}</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="tbb-consent__actions">
          <button
            type="button"
            className="tbb-consent__btn tbb-consent__btn--primary"
            onClick={showPreferences ? savePreferences : acceptAll}
          >
            {showPreferences ? "Save choices" : "Accept all"}
          </button>
          <button
            type="button"
            className="tbb-consent__btn tbb-consent__btn--secondary"
            onClick={rejectAll}
          >
            Reject all
          </button>
          {!showPreferences && (
            <button
              type="button"
              className="tbb-consent__btn tbb-consent__btn--link"
              onClick={() => setShowPreferences(true)}
            >
              Preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
